import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography,
  Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Tooltip, CircularProgress, Paper, Button,
  Select, MenuItem, FormControl,
} from '@mui/material';
import { selectFieldSx, dropdownMenuProps } from '../utils/dropdownStyles';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import ContentArea from '../components/content-area/Content';
import ItemCard from '../components/items/ItemCard';
import ItemModal from '../components/items/ItemModal';
import DiscountModal from '../components/items/DiscountModal';
import CustomDatePicker from '../components/form-components/CustomDatePicker';
import FreeTrialListedItemCard from '../components/items/FreeTrialListedItemCard';
import ConfirmModal from '../components/form-components/ConfirmModal';
import { useCurrency } from '../context/CurrencyContext';
import {
  getItems, getItemById, createItem, updateItem, deleteItem,
  getDiscounts, createDiscount, updateDiscount, deleteDiscount,
  getTaxCategories, createTaxClass,
  getItemTypes,
  getMerchandiseTypes, getMerchandiseCategories, getTicketTypes,
  getFreeTrialSetting, createFreeTrialSetting, updateFreeTrialSetting,
  getFreeTrialItems, removeFreeTrialItem, removeFreeTrialPackage,
  getFreeTrialClaims,
} from '../api/itemApi';
import { getEvents } from '../api/eventApi';
import { getPackages } from '../api/packageApi';
import { submitApproval } from '../api/approvalApi';
import { useToast } from '../context/ToastContext';
import ItemsIconPng from '../assets/icons/items.png';
import LocalText from '../components/common/LocalText';
import { useLocalText } from '../components/common/useLocalText';
import { useSpaceId } from '../hooks/useSpaceId';
import { usePermissions } from '../hooks/usePermissions';
import StatusTabs from '../components/approval/StatusTabs';
import { buildVersionedList, filterVersionedList, countVersionedTabs } from '../utils/versionedContent';
import ApprovalStatusBadge from '../components/approval/ApprovalStatusBadge';
import SubmitForApprovalButton from '../components/approval/SubmitForApprovalButton';
import WithdrawButton from '../components/approval/WithdrawButton';
import ContentPreviewDrawer from '../components/approval/ContentPreviewDrawer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { signalRService } from '../services/signalRService';
import { SIGNALR_EVENTS } from '../constants/signalREvents';
import { CONTENT_TYPES } from '../constants/contentTypes';

// Renders a single <option> with a translated label.
// Must be a component (not inline) so useLocalText can obey hook rules.
const LocalOption = ({ value, children }) => {
  const label = useLocalText(children ?? '');
  return <option value={value}>{label}</option>;
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getDiscountStatus = (discount) => {
  const today = new Date().toISOString().slice(0, 10);
  const start = discount.startAt ? String(discount.startAt).slice(0, 10) : null;
  const end   = discount.endAt   ? String(discount.endAt).slice(0, 10)   : null;
  if (start && today < start) return 'Upcoming';
  if (end   && today > end)   return 'Expired';
  return 'Active';
};

const STATUS_CHIP = {
  Active:   { bg: '#dcfce7', color: '#15803d' },
  Upcoming: { bg: '#dbeafe', color: '#1d4ed8' },
  Expired:  { bg: '#f1f5f9', color: '#64748b' },
};

// Format date strings → "2026-06-10"  (date-only values from the API)
const fmtDt = (dt) => {
  if (!dt) return '—';
  return String(dt).slice(0, 10);
};

const TABS = [
  { key: 'items',            label: 'Item Catalog',      icon: <SellOutlinedIcon sx={{ fontSize: 14 }} /> },
  { key: 'discounts',        label: 'Discount Scheduler', icon: <PercentIcon sx={{ fontSize: 14 }} /> },
  { key: 'freeTrial',        label: 'Free Trial',         icon: <CardGiftcardOutlinedIcon sx={{ fontSize: 14 }} /> },
  { key: 'freeTrialClaims',  label: 'Free Trial Claims',  icon: <HistoryOutlinedIcon sx={{ fontSize: 14 }} /> },
  { key: 'prizes',           label: 'Prizes',             icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 14 }} /> },
  { key: 'packages',         label: 'Packages',           icon: <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />, route: '/packages' },
];

const cardSx = {
  bgcolor: 'white',
  borderRadius: '12px',
  border: '1px solid rgba(226,232,240,0.8)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

// Table header column definitions for the discount scheduler
const DISC_COLUMNS = [
  'Item Name',
  'Campaign Name',
  'Discount Magnitude',
  'Schedule Timeframe',
  'Status',
  'Approval',
  'Actions',
];

// Shared pill-select container (matches design's inline-label filter style)
const PillSelect = ({ label, value, onChange, children, disabled }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: disabled ? 0.55 : 1 }}>
    <Typography fontSize={12} fontWeight={600} sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <FormControl size="small" disabled={disabled}>
      <Select
        value={value}
        onChange={onChange}
        MenuProps={dropdownMenuProps}
        sx={{
          ...selectFieldSx,
          fontSize: 12, fontWeight: 700, color: '#0a326d',
          '& .MuiSelect-select': { py: '7px', pl: 1.5, pr: 3.5, fontSize: 12, fontWeight: 700 },
        }}
      >
        {children}
      </Select>
    </FormControl>
  </Box>
);

// ── Page ──────────────────────────────────────────────────────────────────────
const Items = () => {
  const spaceId = useSpaceId();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/items', 'items-edit');
  const canEditFreeTrial = canFeature('/items', 'free-trial-edit');
  const { activeCurrency, currencies } = useCurrency();
  const decimalPlaces  = activeCurrency?.decimalPlaces ?? 2;
  const factor         = Math.pow(10, decimalPlaces);
  const symbol         = activeCurrency?.symbol ?? '';

  // Localized strings used in non-JSX contexts (native <option> elements, etc.)
  const allTypesLabel = useLocalText('All Types');

  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab,        setActiveTab]        = useState(location.state?.activeTab ?? 'items');
  const [statusTab,        setStatusTab]        = useState('live');
  const [discountStatusTab, setDiscountStatusTab] = useState('live');
  const [prizeStatusTab,   setPrizeStatusTab]    = useState('live');
  const [itemHighlight,     setItemHighlight]     = useState({ tab: null, token: 0 });
  const [discountHighlight, setDiscountHighlight] = useState({ tab: null, token: 0 });
  const [prizeHighlight,    setPrizeHighlight]    = useState({ tab: null, token: 0 });
  const flashItemTab     = useCallback((tab) => setItemHighlight({ tab, token: Date.now() }), []);
  const flashDiscountTab = useCallback((tab) => setDiscountHighlight({ tab, token: Date.now() }), []);
  const flashPrizeTab    = useCallback((tab) => setPrizeHighlight({ tab, token: Date.now() }), []);
  const [items,               setItems]               = useState([]);
  const [packages,            setPackages]            = useState([]);
  const [discounts,           setDiscounts]           = useState([]);
  const [taxCategories,       setTaxCategories]       = useState([]);
  // Filters out Avatar (6, created only from the Package "Create New Avatar" tab), Reward
  // (4) and Digital Collectible (5, neither offered as a general-purpose creatable type),
  // and Package (7, has its own dedicated flow) — used by both the Item Catalog tab's
  // create modal and the Prizes tab's, so a prize can be any real catalog type.
  const [itemTypes,             setItemTypes]             = useState([]);
  const [merchandiseTypes,    setMerchandiseTypes]    = useState([]);
  const [merchandiseCategories, setMerchandiseCategories] = useState([]);
  const [ticketTypes,         setTicketTypes]         = useState([]);
  const [events,              setEvents]              = useState([]);
  const [loading,             setLoading]             = useState(true);

  // ── Filters ───────────────────────────────────────────────────────────────
  const [search,         setSearch]         = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState('All');

  // ── Modal state ────────────────────────────────────────────────────────────
  const [itemModalOpen,     setItemModalOpen]     = useState(false);
  const [itemModalReadOnly, setItemModalReadOnly] = useState(false);
  const [itemProposeMode,   setItemProposeMode]   = useState(false);
  const [editingItem,       setEditingItem]       = useState(null);
  const [discountModalOpen,    setDiscountModalOpen]    = useState(false);
  const [editingDiscount,      setEditingDiscount]      = useState(null);
  const [discountProposeMode,   setDiscountProposeMode]   = useState(false);
  const [discountModalReadOnly, setDiscountModalReadOnly] = useState(false);
  const [discountViewProposal,  setDiscountViewProposal]  = useState({ open: false, record: null, liveId: null });
  const [deleteTarget,      setDeleteTarget]      = useState(null); // { type, data }
  const [proposeDeleteItem, setProposeDeleteItem] = useState(null);

  // ── Prizes ────────────────────────────────────────────────────────────────
  // Prize-only items are real catalog items (is_price_only=1) and go through the
  // exact same content-approval lifecycle as the Item Catalog tab — same ItemCard,
  // same StatusTabs, same submitApproval/approve/reject — just a separate tab
  // showing only items.filter(isPriceOnly), so a draft/pending prize can be
  // reviewed before it's ever grantable (see sp_Item_SelectPrizeOnlyBySpace's own
  // approved-only filter, which is what the Announcement grant flow reads from).
  const [prizeModalOpen,      setPrizeModalOpen]      = useState(false);
  const [prizeModalReadOnly,  setPrizeModalReadOnly]  = useState(false);
  const [prizeProposeMode,    setPrizeProposeMode]    = useState(false);
  const [editingPrizeItem,    setEditingPrizeItem]    = useState(null);
  const [proposeDeletePrize,  setProposeDeletePrize]  = useState(null);

  // ── Free Trial ──────────────────────────────────────────────────────────
  const [freeTrialSetting, setFreeTrialSetting] = useState(null);
  const [freeTrialItems,   setFreeTrialItems]   = useState([]);
  const [freeTrialClaims,  setFreeTrialClaims]  = useState([]);
  const [freeTrialForm,    setFreeTrialForm]    = useState({ startAt: '', endAt: '', isEnabled: false });
  const freeTrialTodayIso = new Date().toISOString().slice(0, 10);
  const [freeTrialSaving,  setFreeTrialSaving]  = useState(false);
  const [freeTrialItemStatusTab, setFreeTrialItemStatusTab] = useState('live');
  const [freeTrialItemHighlight, setFreeTrialItemHighlight] = useState({ tab: null, token: 0 });
  const flashFreeTrialItemTab = useCallback((tab) => setFreeTrialItemHighlight({ tab, token: Date.now() }), []);
  const [freeTrialViewProposal, setFreeTrialViewProposal] = useState({ open: false, record: null, liveId: null, contentType: null });
  const [freeTrialFormExpanded, setFreeTrialFormExpanded] = useState(false);

  // ── Load data on mount / space switch ─────────────────────────────────────
  useEffect(() => {
    if (!spaceId) return;
    setLoading(true);
    Promise.all([
      getItems().catch(() => null),
      getPackages().catch(() => null),
      getDiscounts().catch(() => null),
      getTaxCategories().catch(() => null),
      getItemTypes().catch(() => null),
      getMerchandiseTypes().catch(() => null),
      getMerchandiseCategories().catch(() => null),
      getTicketTypes().catch(() => null),
      getEvents().catch(() => null),
      getFreeTrialSetting().catch(() => null),
      getFreeTrialItems().catch(() => null),
      getFreeTrialClaims().catch(() => null),
    ]).then(([itemsData, packagesData, discountsData, taxData, itTypes, merchTypes, merchCats, tickTypes, eventsData, freeTrialSettingData, freeTrialItemsData, freeTrialClaimsData]) => {
      if (Array.isArray(itemsData))     setItems(itemsData);
      if (Array.isArray(packagesData))  setPackages(packagesData);
      if (Array.isArray(discountsData)) setDiscounts(discountsData);
      if (Array.isArray(taxData)    && taxData.length > 0)    setTaxCategories(taxData);
      // See the itemTypes state comment above for why 4/5/6/7 are excluded.
      if (Array.isArray(itTypes)    && itTypes.length > 0) {
        setItemTypes(itTypes.filter(t => ![4, 5, 6, 7].includes(t.id)));
      }
      if (Array.isArray(merchTypes) && merchTypes.length > 0) setMerchandiseTypes(merchTypes);
      if (Array.isArray(merchCats)  && merchCats.length > 0)  setMerchandiseCategories(merchCats);
      if (Array.isArray(tickTypes)  && tickTypes.length > 0)  setTicketTypes(tickTypes);
      if (Array.isArray(eventsData))    setEvents(eventsData);
      if (freeTrialSettingData) {
        setFreeTrialSetting(freeTrialSettingData);
        setFreeTrialForm({
          startAt:   fmtDt(freeTrialSettingData.startAt),
          endAt:     fmtDt(freeTrialSettingData.endAt),
          isEnabled: !!freeTrialSettingData.isEnabled,
        });
      }
      if (Array.isArray(freeTrialItemsData))  setFreeTrialItems(freeTrialItemsData);
      if (Array.isArray(freeTrialClaimsData)) setFreeTrialClaims(freeTrialClaimsData);
    }).finally(() => setLoading(false));
  }, [spaceId]);

  // ── Free Trial handlers ─────────────────────────────────────────────────
  const reloadFreeTrialSetting = useCallback(async () => {
    const data = await getFreeTrialSetting().catch(() => null);
    if (data) {
      setFreeTrialSetting(data);
      setFreeTrialForm({ startAt: fmtDt(data.startAt), endAt: fmtDt(data.endAt), isEnabled: !!data.isEnabled });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadFreeTrialItems = useCallback(async () => {
    const data = await getFreeTrialItems().catch(() => null);
    if (Array.isArray(data)) setFreeTrialItems(data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Setting has exactly one row per space — same 'draft goes live only after being approved'
  // rule as everything else, but create vs. edit is decided server-side (Create when none
  // exists yet, Edit-proposal once an approved row exists) so the client just always calls
  // create the first time and update afterward, same as the Discounts modal does.
  const handleSaveFreeTrialSetting = useCallback(async () => {
    if (!freeTrialForm.startAt || !freeTrialForm.endAt) {
      toast.error('Start and end date are required.');
      return false;
    }
    setFreeTrialSaving(true);
    try {
      const payload = {
        startAt:   freeTrialForm.startAt,
        endAt:     freeTrialForm.endAt,
        isEnabled: freeTrialForm.isEnabled,
      };
      if (freeTrialSetting) {
        const result = await updateFreeTrialSetting(payload);
        if (result?.message) {
          // 202 — backend submitted an edit proposal (setting was already approved)
          toast.success(result.message);
        } else {
          toast.success('Free trial campaign updated.');
        }
      } else {
        await createFreeTrialSetting(payload);
        toast.success('Free trial campaign created — submit it for approval below to go live.');
      }
      await reloadFreeTrialSetting();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save the free trial campaign.');
      return false;
    } finally {
      setFreeTrialSaving(false);
    }
  }, [freeTrialForm, freeTrialSetting, toast, reloadFreeTrialSetting]);

  // Adding items now happens on the dedicated /items/free-trial/add page.
  // A free-trial list entry is either an item (itemId set) or a package
  // (packageId set, itemId null) — each has its own remove endpoint, so this
  // must branch instead of always calling the item remover with a null id.
  const handleRemoveFreeTrialItem = useCallback(async (itemId, packageId) => {
    try {
      const result = packageId != null
        ? await removeFreeTrialPackage(packageId)
        : await removeFreeTrialItem(itemId);
      if (result?.message) {
        toast.success(result.message);
        flashFreeTrialItemTab('pending');
      } else {
        toast.success(packageId != null
          ? 'Package removed from the free trial list.'
          : 'Item removed from the free trial list.');
      }
      await reloadFreeTrialItems();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to remove the item from the free trial list.');
    }
  }, [toast, reloadFreeTrialItems, flashFreeTrialItemTab]);

  // ── Derived data ───────────────────────────────────────────────────────────
  // Avatar items (itemTypeId 6) are hidden from this page — hotfix, managed only
  // via the Package "Create New Avatar" tab. Revert by dropping this filter.
  // Prize-only items (isPrizeOnly) are excluded here too — they have their own tab
  // below, sharing this same `items` fetch (usp_Item_SelectBySpace no longer
  // filters is_price_only out, so both tabs derive from one call) but filtered the
  // other way, so an item never appears in both lists at once.
  const itemVersioned = useMemo(
    () => buildVersionedList(items.filter(it => it.itemTypeId !== 6 && !it.isPrizeOnly), 'itemId'),
    [items]
  );
  // Scoped to the current Item Type filter so tab counts (Live/Pending/Draft/Rejected)
  // match what filteredItems actually shows, instead of always reflecting all types.
  const itemCounts = useMemo(() => {
    const scoped = itemTypeFilter === 'All'
      ? itemVersioned
      : itemVersioned.filter(item => item.itemTypeId === Number(itemTypeFilter));
    return {
      all:      scoped.length,
      draft:    filterVersionedList(scoped, 'draft').length,
      pending:  filterVersionedList(scoped, 'pending').length,
      live:     filterVersionedList(scoped, 'live').length,
      rejected: filterVersionedList(scoped, 'rejected').length,
    };
  }, [itemVersioned, itemTypeFilter]);

  const filteredItems = useMemo(() => {
    let result = filterVersionedList(itemVersioned, statusTab);
    const q = search.toLowerCase();
    if (itemTypeFilter !== 'All') result = result.filter(item => item.itemTypeId === Number(itemTypeFilter));
    if (!q) return result;
    return result.filter(item => {
      const name = item.nameLocalizations?.values?.[0]?.value ?? item.name ?? '';
      const desc = item.descriptionLocalizations?.values?.[0]?.value ?? item.description ?? '';
      return name.toLowerCase().includes(q)
        || (item.sku ?? '').toLowerCase().includes(q)
        || desc.toLowerCase().includes(q);
    });
  }, [itemVersioned, search, itemTypeFilter, statusTab]);

  // Prizes tab — same `items` fetch as above, filtered the other way (isPrizeOnly only),
  // through the exact same buildVersionedList/filterVersionedList/countVersionedTabs
  // pipeline the Item Catalog tab uses. No item-type or search filter here (kept simple,
  // unlike Item Catalog's) — just the Live/Pending/Draft/Rejected lifecycle.
  const prizeVersioned = useMemo(
    () => buildVersionedList(items.filter(it => it.isPrizeOnly), 'itemId'),
    [items]
  );
  const prizeCounts    = useMemo(() => countVersionedTabs(items.filter(it => it.isPrizeOnly), 'itemId'), [items]);
  const filteredPrizes = useMemo(
    () => filterVersionedList(prizeVersioned, prizeStatusTab),
    [prizeVersioned, prizeStatusTab]
  );

  const discountVersioned = useMemo(() => buildVersionedList(discounts, 'id'), [discounts]);
  const discountCounts    = useMemo(() => countVersionedTabs(discounts, 'id'), [discounts]);

  const filteredDiscounts = useMemo(() => filterVersionedList(discountVersioned, discountStatusTab), [discountVersioned, discountStatusTab]);

  const currentData = activeTab === 'items' ? filteredItems : filteredDiscounts;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleClearFilters = () => {
    setSearch('');
    setItemTypeFilter('All');
  };

  const handleItemSave = async (formData) => {
    try {
      if (itemProposeMode) {
        const itemId = formData.itemId ?? editingItem?.itemId;
        const itemName = formData.nameLocalizations?.values?.[0]?.value ?? formData.name ?? `Item ${itemId}`;
        await submitApproval({
          contentType: CONTENT_TYPES.ITEM,
          contentId: itemId,
          contentName: itemName,
          actionType: 'edit',
          proposedData: JSON.stringify(formData),
          proposedSnapshot: JSON.stringify(formData),
        });
        toast.success('Edit submitted for approval!');
        setItemProposeMode(false);
        setItemModalOpen(false);
        setEditingItem(null);
        const refreshed = await getItemById(itemId).catch(() => null);
        if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
        flashItemTab('pending');
      } else if (formData.itemId) {
        const { itemId, sku, ...updatePayload } = formData;
        const updated = await updateItem(itemId, updatePayload);
        setItems(prev => prev.map(it => it.itemId === itemId ? { ...it, ...updated } : it));
        toast.success('Item updated successfully.');
        setItemModalOpen(false);
        setEditingItem(null);
        flashItemTab('draft');
      } else {
        const { itemId: _id, ...createPayload } = formData;
        const created = await createItem(createPayload);
        setItems(prev => [...prev, created]);
        toast.success('Item registered successfully.');
        setItemModalOpen(false);
        setEditingItem(null);
        flashItemTab('draft');
      }
    } catch {
      // global interceptor already shows the error toast
    }
  };

  const handleItemEditRejected = (proposedSnap, liveItemId) => {
    const liveItem = items.find(it => it.itemId === liveItemId);
    setEditingItem({ ...(liveItem ?? {}), ...proposedSnap, itemId: liveItemId });
    setItemProposeMode(true);
    setItemModalReadOnly(false);
    setItemModalOpen(true);
  };

  // Prizes tab — identical shape to handleItemSave above, just its own modal/state names
  // (editingPrizeItem, prizeProposeMode, etc.) so it can't interact with the Item Catalog
  // tab's own edit/propose/read-only state. is_price_only itself is immutable after
  // creation (ItemModal's forcePrizeOnly always forces isPrizeOnly:true / baseCost:0 in
  // the payload here — there is no toggle to change it on an edit).
  const handlePrizeSave = async (formData) => {
    try {
      if (prizeProposeMode) {
        const itemId = formData.itemId ?? editingPrizeItem?.itemId;
        const itemName = formData.nameLocalizations?.values?.[0]?.value ?? formData.name ?? `Item ${itemId}`;
        await submitApproval({
          contentType: CONTENT_TYPES.ITEM,
          contentId: itemId,
          contentName: itemName,
          actionType: 'edit',
          proposedData: JSON.stringify(formData),
          proposedSnapshot: JSON.stringify(formData),
        });
        toast.success('Edit submitted for approval!');
        setPrizeProposeMode(false);
        setPrizeModalOpen(false);
        setEditingPrizeItem(null);
        const refreshed = await getItemById(itemId).catch(() => null);
        if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
        flashPrizeTab('pending');
      } else if (formData.itemId) {
        const { itemId, sku, ...updatePayload } = formData;
        const updated = await updateItem(itemId, updatePayload);
        setItems(prev => prev.map(it => it.itemId === itemId ? { ...it, ...updated } : it));
        toast.success('Prize item updated successfully.');
        setPrizeModalOpen(false);
        setEditingPrizeItem(null);
        flashPrizeTab('draft');
      } else {
        const { itemId: _id, ...createPayload } = formData;
        const created = await createItem(createPayload);
        setItems(prev => [...prev, created]);
        toast.success('Prize item created.');
        setPrizeModalOpen(false);
        setEditingPrizeItem(null);
        flashPrizeTab('draft');
      }
    } catch {
      // global interceptor already shows the error toast
    }
  };

  const handlePrizeEditRejected = (proposedSnap, liveItemId) => {
    const liveItem = items.find(it => it.itemId === liveItemId);
    setEditingPrizeItem({ ...(liveItem ?? {}), ...proposedSnap, itemId: liveItemId });
    setPrizeProposeMode(true);
    setPrizeModalReadOnly(false);
    setPrizeModalOpen(true);
  };

  const confirmPrizeProposeDelete = async () => {
    if (!proposeDeletePrize) return;
    try {
      const itemName = proposeDeletePrize.nameLocalizations?.values?.[0]?.value ?? proposeDeletePrize.name ?? `Item ${proposeDeletePrize.itemId}`;
      await submitApproval({
        contentType: CONTENT_TYPES.ITEM,
        contentId: proposeDeletePrize.itemId,
        contentName: itemName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeletePrize(null);
      const data = await getItems().catch(() => null);
      if (Array.isArray(data)) setItems(data);
      flashPrizeTab('pending');
    } catch {
      setProposeDeletePrize(null);
    }
  };

  const handleDiscountEditRejected = (proposedSnap, liveDiscountId) => {
    setEditingDiscount({ ...proposedSnap, id: liveDiscountId });
    setDiscountProposeMode(true);
    setDiscountModalOpen(true);
  };

  const confirmItemProposeDelete = async () => {
    if (!proposeDeleteItem) return;
    try {
      const itemName = proposeDeleteItem.nameLocalizations?.values?.[0]?.value ?? proposeDeleteItem.name ?? `Item ${proposeDeleteItem.itemId}`;
      await submitApproval({
        contentType: CONTENT_TYPES.ITEM,
        contentId: proposeDeleteItem.itemId,
        contentName: itemName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteItem(null);
      const data = await getItems().catch(() => null);
      if (Array.isArray(data)) setItems(data);
      flashItemTab('pending');
    } catch {
      setProposeDeleteItem(null);
    }
  };

  const handleDiscountSave = async (formData) => {
    const { id, ...payload } = formData;
    try {
      if (discountProposeMode) {
        const discountId = id ?? editingDiscount?.id;
        const discountName =
          payload.nameLocalizations?.find?.(l => l.localeId?.startsWith('en'))?.value ||
          payload.nameLocalizations?.[0]?.value ||
          `Discount ${discountId}`;
        await submitApproval({
          contentType:      CONTENT_TYPES.DISCOUNT,
          contentId:        discountId,
          contentName:      discountName,
          actionType:       'edit',
          proposedData:     JSON.stringify({ ...payload, id: discountId }),
          proposedSnapshot: JSON.stringify({ ...payload, id: discountId }),
        });
        toast.success('Edit submitted for approval!');
        setDiscountProposeMode(false);
        setDiscountModalOpen(false);
        setEditingDiscount(null);
        await reloadDiscounts();
        flashDiscountTab('pending');
        return;
      }
      let targetTab = 'draft';
      if (id) {
        const updatePayload = {
          discountTypeId:    payload.discountTypeId,
          nameLocalizations: payload.nameLocalizations,
          ratePct:           payload.ratePct,
          amount:            payload.amount,
          startAt:           payload.startAt,
          endAt:             payload.endAt,
        };
        const updated = await updateDiscount(id, updatePayload);
        if (updated?.message) {
          // 202 — backend submitted an edit proposal (discount was approved/rejected-non-create)
          toast.success(updated.message);
          targetTab = 'pending';
        } else {
          const row = updated?.discount ?? updated;
          setDiscounts(prev => prev.map(d => d.id === id ? { ...d, ...row } : d));
          toast.success('Discount campaign updated.');
        }
      } else {
        const created = await createDiscount(payload);
        const row = created?.discount ?? created;
        setDiscounts(prev => [...prev, row]);
        toast.success('Discount campaign created and moved to Draft.');
      }
      // Re-fetch the affected item so its embedded discounts array is current
      if (payload.itemId) {
        const refreshed = await getItemById(payload.itemId).catch(() => null);
        if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
      }
      setDiscountModalOpen(false);
      setEditingDiscount(null);
      // Reload discounts so approval status badge reflects the latest state
      const refreshedDiscounts = await getDiscounts().catch(() => null);
      if (Array.isArray(refreshedDiscounts)) setDiscounts(refreshedDiscounts);
      flashDiscountTab(targetTab);
    } catch {
      // global interceptor already shows the error toast
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { type, data } = deleteTarget;
    try {
      if (type === CONTENT_TYPES.ITEM) {
        await deleteItem(data.itemId);
        setItems(prev => prev.filter(it => it.itemId !== data.itemId));
        toast.success('Item removed from catalog.');
      } else if (type === CONTENT_TYPES.FREE_TRIAL_ITEM) {
        await handleRemoveFreeTrialItem(data.itemId, data.packageId);
      } else {
        const deleteResult = await deleteDiscount(data.id);
        if (deleteResult?.message) {
          // 202 — backend submitted a delete proposal (discount was approved/rejected-non-create)
          toast.success(deleteResult.message);
          flashDiscountTab('pending');
        } else {
          // 204 — direct delete completed
          setDiscounts(prev => prev.filter(d => d.id !== data.id));
          toast.success('Discount campaign removed.');
        }
        if (data.itemId) {
          const refreshed = await getItemById(data.itemId).catch(() => null);
          if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
        }
        // Reload so approval status reflects delete proposal state
        const discountData = await getDiscounts().catch(() => null);
        if (Array.isArray(discountData)) setDiscounts(discountData);
      }
    } catch {
      // global interceptor already shows the error toast
    } finally {
      setDeleteTarget(null);
    }
  };

  const reloadDiscounts = useCallback(async () => {
    const data = await getDiscounts().catch(() => null);
    if (Array.isArray(data)) setDiscounts(data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadItems = useCallback(async () => {
    const data = await getItems().catch(() => null);
    if (Array.isArray(data)) setItems(data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleDecision = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.ITEM)     reloadItems();
      if (payload?.contentType === CONTENT_TYPES.DISCOUNT) reloadDiscounts();
      if (payload?.contentType === CONTENT_TYPES.FREE_TRIAL_SETTING) reloadFreeTrialSetting();
      if (payload?.contentType === CONTENT_TYPES.FREE_TRIAL_ITEM)    reloadFreeTrialItems();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, handleDecision);
  }, [reloadItems, reloadDiscounts, reloadFreeTrialSetting, reloadFreeTrialItems]);

  useEffect(() => {
    const handleWithdrawn = (payload) => {
      if (payload?.contentType === CONTENT_TYPES.ITEM)     reloadItems();
      if (payload?.contentType === CONTENT_TYPES.DISCOUNT) reloadDiscounts();
      if (payload?.contentType === CONTENT_TYPES.FREE_TRIAL_SETTING) reloadFreeTrialSetting();
      if (payload?.contentType === CONTENT_TYPES.FREE_TRIAL_ITEM)    reloadFreeTrialItems();
    };
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, handleWithdrawn);
  }, [reloadItems, reloadDiscounts, reloadFreeTrialSetting, reloadFreeTrialItems]);

  const handleAddTaxClass = async (newClass) => {
    const created = await createTaxClass(newClass);
    setTaxCategories(prev => [...prev, created]);
    return created;
  };

  // ── Tab switcher ──────────────────────────────────────────────────────────
  const tabSwitcher = (
    <Box sx={{
      display: 'flex', bgcolor: '#f1f5f9', p: '4px', borderRadius: '10px',
      border: '1px solid #e2e8f0', gap: '2px',
      width: { xs: '100%', sm: 'auto' },
    }}>
      {TABS.map(tab => {
        const active = activeTab === tab.key;
        return (
          <Box
            key={tab.key}
            onClick={() => tab.route ? navigate(tab.route) : setActiveTab(tab.key)}
            sx={{
              px: 2, py: 0.875, borderRadius: '8px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
              fontSize: 11, fontWeight: 700, transition: 'all 0.15s', userSelect: 'none',
              flex: { xs: 1, sm: 'none' },
              bgcolor: active ? 'white' : 'transparent',
              color:   active ? '#2563eb' : '#64748b',
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              border:  active ? '1px solid #bfdbfe' : '1px solid transparent',
              '&:hover': { color: active ? '#2563eb' : '#475569', bgcolor: active ? 'white' : '#e8edf4' },
            }}
          >
            {tab.icon}
            <LocalText>{tab.label}</LocalText>
          </Box>
        );
      })}
    </Box>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <ContentArea title="Items" icon={ItemsIconPng} container={false} titleAction={tabSwitcher}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 12 }}>
            <CircularProgress sx={{ color: '#0a326d' }} />
          </Box>
        ) : (
          <>
            {/* ── ITEM CATALOG TAB ─────────────────────────────────────── */}
            {activeTab === 'items' && (
              <>
                {/* Filter bar */}
                <Box
                  sx={{
                    ...cardSx, p: 2,
                    display: 'flex', flexWrap: 'wrap',
                    alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                  }}
                >
                  {/* Search */}
                  <Box sx={{ position: 'relative', flex: { xs: '1 1 100%', sm: '1 1 220px' }, maxWidth: { xs: '100%', sm: 380 } }}>
                    <SearchIcon sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#94a3b8', pointerEvents: 'none' }} />
                    <Box
                      component="input"
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search items..."
                      sx={{
                        width: '100%', pl: '34px', pr: 2, py: '9px',
                        border: '1px solid #e2e8f0', borderRadius: '8px',
                        fontSize: 13, color: '#334155', bgcolor: 'white',
                        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                        '&:focus': { borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6' },
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                    <PillSelect
                      label={<LocalText>Item Type:</LocalText>}
                      value={itemTypeFilter}
                      onChange={e => setItemTypeFilter(e.target.value)}
                    >
                      <MenuItem value="All" sx={{ fontSize: 12 }}>{allTypesLabel}</MenuItem>
                      {itemTypes.map(t => <MenuItem key={t.id} value={t.id} sx={{ fontSize: 12 }}>{t.Type}</MenuItem>)}
                    </PillSelect>

                    {canEdit && (
                      <>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => { setEditingItem(null); setItemModalOpen(true); }}
                          sx={{
                            background: '#2563eb',
                            color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none',
                            borderRadius: 2, px: 2.5, py: 1.125, whiteSpace: 'nowrap',
                            width: { xs: '100%', sm: 'auto' },
                            boxShadow: '0 2px 8px rgba(37,99,235,0.35)',
                            '&:hover': { background: '#1d4ed8', boxShadow: '0 4px 12px rgba(37,99,235,0.45)' },
                          }}
                        >
                          <LocalText>Create Item</LocalText>
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>

                <StatusTabs items={items} counts={itemCounts} value={statusTab} onChange={setStatusTab} sx={{ mb: 0, mt: 1 }} highlightTab={itemHighlight.tab} highlightToken={itemHighlight.token} />

                {/* Card grid or empty state */}
                {currentData.length > 0 ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)',
                      },
                      gap: 2.5,
                    }}
                  >
                    {currentData.map(item => (
                      <ItemCard
                        key={item.itemId}
                        item={item}
                        itemTypes={itemTypes}
                        onEdit={(it, proposeMode) => { setEditingItem(it); setItemModalReadOnly(false); setItemProposeMode(!!proposeMode); setItemModalOpen(true); }}
                        onView={it => { setEditingItem(it); setItemModalReadOnly(true); setItemProposeMode(false); setItemModalOpen(true); }}
                        onDelete={(it, proposeMode) => proposeMode ? setProposeDeleteItem(it) : setDeleteTarget({ type: CONTENT_TYPES.ITEM, data: it })}
                        onEditRejected={canEdit ? handleItemEditRejected : undefined}
                        onSuccess={async (targetTab) => {
                          const refreshed = await getItemById(item.itemId).catch(() => null);
                          if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
                          if (targetTab) flashItemTab(targetTab);
                        }}
                        showActions={canEdit}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      ...cardSx,
                      py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 64, height: 64, bgcolor: '#f1f5f9', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 2, boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
                      }}
                    >
                      <InventoryIcon sx={{ fontSize: 28, color: '#94a3b8' }} />
                    </Box>
                    <Typography fontWeight={700} fontSize={16} sx={{ color: '#475569', mb: 0.75 }}>
                      <LocalText>No Items Found</LocalText>
                    </Typography>
                    {(search || itemTypeFilter !== 'All') && (
                      <Button
                        onClick={handleClearFilters}
                        sx={{
                          mt: 2, fontSize: 11, fontWeight: 600, textTransform: 'none',
                          color: '#2563eb', bgcolor: '#eff6ff', borderRadius: 1.5, px: 2, py: 0.75,
                          '&:hover': { bgcolor: '#dbeafe' },
                        }}
                      >
                        <LocalText>Clear All Filters</LocalText>
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}

            {/* ── DISCOUNT SCHEDULER TAB ───────────────────────────────── */}
            {activeTab === 'discounts' && (
              <>
                {/* Header white card */}
                <Box
                  sx={{
                    ...cardSx, p: 2,
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
                  }}
                >
                  <Box>
                    <Typography fontWeight={700} fontSize={14} sx={{ color: '#334155' }}>
                      <LocalText>Discount Management</LocalText>
                    </Typography>
                    <Typography fontSize={11} sx={{ color: '#94a3b8', mt: 0.25 }}>
                      <LocalText>Manage price discounts for your items.</LocalText>
                    </Typography>
                  </Box>
                  {canEdit && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { setEditingDiscount(null); setDiscountModalOpen(true); }}
                      sx={{
                        background: '#059669',
                        color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none',
                        borderRadius: 2, px: 2.5, py: 1.125, whiteSpace: 'nowrap',
                        boxShadow: '0 2px 8px rgba(5,150,105,0.35)',
                        '&:hover': { background: '#047857', boxShadow: '0 4px 12px rgba(5,150,105,0.45)' },
                      }}
                    >
                      <LocalText>Add Discount</LocalText>
                    </Button>
                  )}
                </Box>

                <StatusTabs items={discounts} counts={discountCounts} value={discountStatusTab} onChange={setDiscountStatusTab} sx={{ mb: 0, mt: 1 }} highlightTab={discountHighlight.tab} highlightToken={discountHighlight.token} />

                {/* Campaign table */}
                <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                  <Box sx={{ overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 620 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        {DISC_COLUMNS.map(h => (
                          <TableCell
                            key={h}
                            sx={{
                              fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
                              letterSpacing: '0.07em', color: '#64748b', py: 1.5, borderColor: '#f1f5f9',
                            }}
                          >
                            <LocalText>{h}</LocalText>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                            <LocalOfferOutlinedIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1.5, display: 'block', mx: 'auto' }} />
                            <Typography fontSize={13} sx={{ color: '#94a3b8' }}>
                              <LocalText>No discount campaigns yet.</LocalText>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        currentData.map(discount => {
                          const linkedItem    = items.find(it => it.itemId === discount.itemId);
                          const status        = getDiscountStatus(discount);
                          const chip          = STATUS_CHIP[status] ?? STATUS_CHIP.Expired;
                          const isSnapshot    = discount._viewType === 'snapshot';
                          const isLocked      = discount.approvalStatus === 'pending' || discount.approvalStatus === 'rejected';
                          const canDirectEdit = discount.contentStatus === 'draft' && !isLocked;
                          const discountName  = discount.nameLocalizations?.values?.[0]?.value
                            ?? discount.nameLocalizations?.[0]?.value;
                          const viewApprovalRecord = discount.approvalId ? {
                            approvalId:      discount.approvalId,
                            actionType:      discount.approvalActionType,
                            contentType:      CONTENT_TYPES.DISCOUNT,
                            sectionLabel:    'Discount Campaign',
                            contentName:     discountName,
                            submittedByName: '',
                            submittedAt:     null,
                            status:          discount.approvalStatus,
                          } : null;

                          const itemCurrencyId   = linkedItem?.prices?.[0]?.currencyId ?? null;
                          const discountCurrency = (itemCurrencyId ? currencies.find(c => c.id === itemCurrencyId) : null) ?? activeCurrency;
                          const dFactor          = Math.pow(10, discountCurrency?.decimalPlaces ?? 2);
                          const dSymbol          = discountCurrency?.symbol ?? symbol;
                          const dDecimals        = discountCurrency?.decimalPlaces ?? 2;

                          const magnitudeLabel = discount.discountTypeId === 1
                            ? `${discount.ratePct}% Off`
                            : discount.amount != null
                              ? `${dSymbol}${(discount.amount / dFactor).toFixed(dDecimals)} Deducted`
                              : '—';

                          return (
                            <TableRow
                              key={discount.id}
                              sx={{ '&:hover': { bgcolor: '#fafafa' }, '&:last-child td': { border: 0 } }}
                            >
                              {/* Target item */}
                              <TableCell sx={{ py: 1.75, borderColor: '#f1f5f9' }}>
                                {linkedItem ? (
                                  <Typography fontSize={13} fontWeight={600} sx={{ color: '#1e293b' }}>
                                    {linkedItem.nameLocalizations?.values?.[0]?.value ?? linkedItem.name}
                                  </Typography>
                                ) : (
                                  <Typography fontSize={12} sx={{ color: '#94a3b8' }}><LocalText>Unknown item</LocalText></Typography>
                                )}
                              </TableCell>

                              {/* Campaign name */}
                              <TableCell sx={{ py: 1.75, borderColor: '#f1f5f9' }}>
                                <Typography fontSize={13} fontWeight={600} sx={{ color: '#1e293b' }}>
                                  {discountName ?? <Box component="span" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 12 }}>—</Box>}
                                </Typography>
                              </TableCell>

                              {/* Magnitude */}
                              <TableCell sx={{ py: 1.75, borderColor: '#f1f5f9' }}>
                                <Typography fontSize={13} fontWeight={700} sx={{ color: '#1e293b' }}>
                                  {magnitudeLabel}
                                </Typography>
                              </TableCell>

                              {/* Schedule */}
                              <TableCell sx={{ py: 1.75, borderColor: '#f1f5f9' }}>
                                <Typography fontSize={12} sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                                  {fmtDt(discount.startAt)} to {fmtDt(discount.endAt)}
                                </Typography>
                              </TableCell>

                              {/* Status badge */}
                              <TableCell sx={{ py: 1.75, borderColor: '#f1f5f9' }}>
                                <Box
                                  sx={{
                                    display: 'inline-block', px: 1.5, py: 0.4,
                                    borderRadius: '999px', bgcolor: chip.bg, color: chip.color,
                                    fontSize: 11, fontWeight: 700,
                                  }}
                                >
                                  <LocalText>{status}</LocalText>
                                </Box>
                              </TableCell>

                              {/* Approval */}
                              <TableCell sx={{ py: 1.75, borderColor: '#f1f5f9' }}>
                                <Box display="flex" flexDirection="column" gap={0.75} alignItems="flex-start">
                                  {/* Live rows show contentStatus; snapshot rows show approvalStatus — same rule as all other content types */}
                                  {(isSnapshot ? discount.approvalStatus : discount.contentStatus) && (
                                    <ApprovalStatusBadge
                                      status={isSnapshot ? discount.approvalStatus : discount.contentStatus}
                                      actionType={discount.approvalActionType}
                                    />
                                  )}

                                  {/* Snapshot row: View button + Withdraw/Dismiss */}
                                  {isSnapshot && viewApprovalRecord && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                                      onClick={() => setDiscountViewProposal({ open: true, record: viewApprovalRecord, liveId: discount._liveId ?? discount.id })}
                                      sx={{
                                        borderColor: '#3b82f6', color: '#1d4ed8', bgcolor: '#eff6ff',
                                        borderRadius: '8px', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700,
                                        textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap',
                                        boxShadow: 'none',
                                        '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
                                      }}
                                    >
                                      View
                                    </Button>
                                  )}
                                  {canEdit && isSnapshot && discount.approvalStatus === 'pending' && discount.approvalId && (
                                    <WithdrawButton approvalId={discount.approvalId} onWithdrawn={() => { reloadDiscounts(); flashDiscountTab(discount.approvalActionType === 'create' ? 'draft' : 'live'); }} />
                                  )}
                                  {canEdit && isSnapshot && discount.approvalStatus === 'rejected' && discount.approvalId && (
                                    <WithdrawButton approvalId={discount.approvalId} onWithdrawn={() => { reloadDiscounts(); flashDiscountTab(discount.approvalActionType === 'create' ? 'draft' : 'live'); }} variant="dismiss" />
                                  )}

                                  {/* Live row: Submit only when truly unlocked draft */}
                                  {canEdit && !isSnapshot && canDirectEdit && (
                                    <SubmitForApprovalButton
                                      contentType={CONTENT_TYPES.DISCOUNT}
                                      contentId={discount.id}
                                      contentName={discountName}
                                      onSubmitted={() => { reloadDiscounts(); flashDiscountTab('pending'); }}
                                    />
                                  )}
                                </Box>
                              </TableCell>

                              {/* Actions */}
                              <TableCell sx={{ py: 1.75, borderColor: '#f1f5f9' }}>
                                {!isSnapshot && canEdit && (
                                  <Box display="flex" gap={0.75}>
                                    {isLocked ? (
                                      <Tooltip title={<LocalText>View campaign (read-only)</LocalText>}>
                                        <IconButton
                                          size="small"
                                          onClick={() => { setEditingDiscount(discount); setDiscountModalReadOnly(true); setDiscountModalOpen(true); }}
                                          sx={{
                                            bgcolor: '#f8fafc', color: '#3b82f6',
                                            borderRadius: '8px', border: '1px solid #bfdbfe',
                                            '&:hover': { bgcolor: '#eff6ff', color: '#1d4ed8', borderColor: '#93c5fd' },
                                          }}
                                        >
                                          <VisibilityIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title={<LocalText>Edit campaign</LocalText>}>
                                        <IconButton
                                          size="small"
                                          onClick={() => { setEditingDiscount(discount); setDiscountModalOpen(true); }}
                                          sx={{
                                            bgcolor: '#f8fafc', color: '#475569',
                                            borderRadius: '8px', border: '1px solid #e2e8f0',
                                            '&:hover': { bgcolor: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
                                          }}
                                        >
                                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title={
                                      isLocked
                                        ? discount.approvalStatus === 'pending'
                                          ? <LocalText>Withdraw to delete</LocalText>
                                          : <LocalText>Dismiss rejected proposal to delete</LocalText>
                                        : <LocalText>Remove campaign</LocalText>
                                    }>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={isLocked}
                                          onClick={() => setDeleteTarget({ type: CONTENT_TYPES.DISCOUNT, data: discount })}
                                          sx={{
                                            bgcolor: '#fff1f2', color: '#ef4444',
                                            borderRadius: '8px', border: '1px solid #fecdd3',
                                            '&:hover': { bgcolor: '#fee2e2', color: '#dc2626', borderColor: '#fca5a5' },
                                            '&.Mui-disabled': { opacity: 0.45 },
                                          }}
                                        >
                                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                  </Box>
                </Paper>
              </>
            )}

            {/* ── FREE TRIAL TAB ───────────────────────────────────────── */}
            {activeTab === 'freeTrial' && (() => {
              const freeTrialItemVersioned = buildVersionedList(freeTrialItems, 'id');
              const freeTrialItemCounts    = countVersionedTabs(freeTrialItems, 'id');
              const freeTrialItemRows      = filterVersionedList(freeTrialItemVersioned, freeTrialItemStatusTab);
              const settingViewApprovalRecord = freeTrialSetting?.approvalId ? {
                approvalId:      freeTrialSetting.approvalId,
                actionType:      freeTrialSetting.approvalActionType,
                contentType:     CONTENT_TYPES.FREE_TRIAL_SETTING,
                sectionLabel:    'Free Trial Campaign',
                contentName:     'Free Trial Campaign',
                submittedByName: '',
                submittedAt:     null,
                status:          freeTrialSetting.approvalStatus,
              } : null;
              const settingCanDirectEdit = freeTrialSetting?.contentStatus === 'draft'
                && freeTrialSetting?.approvalStatus !== 'pending' && freeTrialSetting?.approvalStatus !== 'rejected';
              // Step 1: no campaign yet — only the create form + empty state, nothing else.
              // Step 2: campaign exists — collapse to a summary strip (Edit reopens the form),
              // unlock the item grid below it. See flow discussed for the Free Trial tab redesign.
              const showForm = !freeTrialSetting || freeTrialFormExpanded;

              return (
                <>
                  <Box sx={{ ...cardSx, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography fontWeight={700} fontSize={14} sx={{ color: '#334155', mb: 0.5 }}>
                          <LocalText>Free Trial Campaign</LocalText>
                        </Typography>
                        {showForm && (
                          <Typography fontSize={11} sx={{ color: '#94a3b8' }}>
                            <LocalText>Set the trial window. Every claimer's trial ends on the same end date, regardless of when they claimed.</LocalText>
                          </Typography>
                        )}
                      </Box>
                      {freeTrialSetting?.contentStatus && (
                        <ApprovalStatusBadge
                          status={freeTrialSetting.approvalStatus === 'pending' || freeTrialSetting.approvalStatus === 'rejected' ? freeTrialSetting.approvalStatus : freeTrialSetting.contentStatus}
                          actionType={freeTrialSetting.approvalActionType}
                        />
                      )}
                    </Box>

                    {showForm ? (
                      <>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mt: 2 }}>
                          <Box sx={!canEditFreeTrial ? { pointerEvents: 'none', opacity: 0.82, userSelect: 'none' } : {}}>
                            <Typography fontSize={11} sx={{ color: '#64748b', mb: 0.5 }}><LocalText>Start Date</LocalText></Typography>
                            <CustomDatePicker
                              value={freeTrialForm.startAt}
                              onChange={(e) => setFreeTrialForm((f) => ({ ...f, startAt: e.target.value }))}
                              min={freeTrialTodayIso}
                              placeholder="Select start date"
                            />
                          </Box>
                          <Box sx={!canEditFreeTrial ? { pointerEvents: 'none', opacity: 0.82, userSelect: 'none' } : {}}>
                            <Typography fontSize={11} sx={{ color: '#64748b', mb: 0.5 }}><LocalText>End Date</LocalText></Typography>
                            <CustomDatePicker
                              value={freeTrialForm.endAt}
                              onChange={(e) => setFreeTrialForm((f) => ({ ...f, endAt: e.target.value }))}
                              min={freeTrialForm.startAt || freeTrialTodayIso}
                              placeholder="Select end date"
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                              type="checkbox"
                              id="freeTrialEnabled"
                              checked={freeTrialForm.isEnabled}
                              disabled={!canEditFreeTrial}
                              onChange={(e) => setFreeTrialForm((f) => ({ ...f, isEnabled: e.target.checked }))}
                            />
                            <label htmlFor="freeTrialEnabled" style={{ fontSize: 12, color: '#334155' }}>
                              <LocalText>Enabled</LocalText>
                            </label>
                          </Box>
                          {canEditFreeTrial && (
                            <Button
                              variant="contained"
                              onClick={async () => { const ok = await handleSaveFreeTrialSetting(); if (ok) setFreeTrialFormExpanded(false); }}
                              disabled={freeTrialSaving}
                              sx={{
                                background: '#059669', color: 'white', fontWeight: 700, fontSize: 12,
                                textTransform: 'none', borderRadius: 2, px: 2.5, py: 1.125,
                                '&:hover': { background: '#047857' },
                              }}
                            >
                              <LocalText>{freeTrialSetting ? 'Save Changes' : 'Create Campaign'}</LocalText>
                            </Button>
                          )}
                          {freeTrialSetting && (
                            <Button
                              variant="text"
                              onClick={() => setFreeTrialFormExpanded(false)}
                              sx={{ color: '#64748b', fontWeight: 700, fontSize: 12, textTransform: 'none' }}
                            >
                              <LocalText>Cancel</LocalText>
                            </Button>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                          {settingViewApprovalRecord && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                              onClick={() => setFreeTrialViewProposal({ open: true, record: settingViewApprovalRecord, liveId: freeTrialSetting.id, contentType: CONTENT_TYPES.FREE_TRIAL_SETTING })}
                              sx={{
                                borderColor: '#3b82f6', color: '#1d4ed8', bgcolor: '#eff6ff',
                                borderRadius: '8px', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap', boxShadow: 'none',
                                '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
                              }}
                            >
                              View Proposal
                            </Button>
                          )}
                          {canEditFreeTrial && freeTrialSetting?.approvalStatus === 'pending' && freeTrialSetting?.approvalId && (
                            <WithdrawButton approvalId={freeTrialSetting.approvalId} onWithdrawn={reloadFreeTrialSetting} />
                          )}
                          {canEditFreeTrial && freeTrialSetting?.approvalStatus === 'rejected' && freeTrialSetting?.approvalId && (
                            <WithdrawButton approvalId={freeTrialSetting.approvalId} onWithdrawn={reloadFreeTrialSetting} variant="dismiss" />
                          )}
                          {canEditFreeTrial && settingCanDirectEdit && (
                            <SubmitForApprovalButton
                              contentType={CONTENT_TYPES.FREE_TRIAL_SETTING}
                              contentId={freeTrialSetting.id}
                              contentName="Free Trial Campaign"
                              onSubmitted={reloadFreeTrialSetting}
                            />
                          )}
                        </Box>

                        {!freeTrialSetting && (
                          <Typography fontSize={11} sx={{ color: '#94a3b8', mt: 2 }}>
                            <LocalText>Create a campaign to start selecting trial-eligible items.</LocalText>
                          </Typography>
                        )}
                      </>
                    ) : (
                      // Collapsed summary strip — campaign already exists.
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                        <Typography fontSize={13} fontWeight={600} sx={{ color: '#1e293b', fontFamily: 'monospace' }}>
                          {fmtDt(freeTrialSetting.startAt)} → {fmtDt(freeTrialSetting.endAt)}
                        </Typography>
                        <Box
                          sx={{
                            fontSize: 11, fontWeight: 700, borderRadius: 999, px: 1.25, py: 0.375,
                            ...(freeTrialSetting.isEnabled ? STATUS_CHIP.Active : STATUS_CHIP.Expired),
                          }}
                        >
                          <LocalText>{freeTrialSetting.isEnabled ? 'Enabled' : 'Disabled'}</LocalText>
                        </Box>
                        {canEditFreeTrial && (
                          <Tooltip title={<LocalText>Edit campaign</LocalText>}>
                            <IconButton size="small" onClick={() => setFreeTrialFormExpanded(true)} sx={{ color: '#64748b', '&:hover': { bgcolor: '#eff6ff', color: '#1d4ed8' } }}>
                              <EditOutlinedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {settingViewApprovalRecord && (
                          <Button
                            variant="outlined" size="small"
                            startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                            onClick={() => setFreeTrialViewProposal({ open: true, record: settingViewApprovalRecord, liveId: freeTrialSetting.id, contentType: CONTENT_TYPES.FREE_TRIAL_SETTING })}
                            sx={{ borderColor: '#3b82f6', color: '#1d4ed8', bgcolor: '#eff6ff', borderRadius: '8px', px: 1.25, py: 0.4, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', boxShadow: 'none', '&:hover': { bgcolor: '#dbeafe' } }}
                          >
                            View Proposal
                          </Button>
                        )}
                        {canEditFreeTrial && freeTrialSetting?.approvalStatus === 'pending' && freeTrialSetting?.approvalId && (
                          <WithdrawButton approvalId={freeTrialSetting.approvalId} onWithdrawn={reloadFreeTrialSetting} />
                        )}
                        {canEditFreeTrial && freeTrialSetting?.approvalStatus === 'rejected' && freeTrialSetting?.approvalId && (
                          <WithdrawButton approvalId={freeTrialSetting.approvalId} onWithdrawn={reloadFreeTrialSetting} variant="dismiss" />
                        )}
                        {canEditFreeTrial && settingCanDirectEdit && (
                          <SubmitForApprovalButton
                            contentType={CONTENT_TYPES.FREE_TRIAL_SETTING}
                            contentId={freeTrialSetting.id}
                            contentName="Free Trial Campaign"
                            onSubmitted={reloadFreeTrialSetting}
                          />
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Item grid only unlocks once a campaign exists — step 2 of the flow. */}
                  {freeTrialSetting && (
                    <Paper sx={{ ...cardSx, mt: 2, overflow: 'hidden' }}>
                      <Box sx={{ p: 2, borderBottom: '1px solid rgba(226,232,240,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                        <Box>
                          <Typography fontWeight={700} fontSize={14} sx={{ color: '#334155' }}>
                            <LocalText>Free Trial Items</LocalText>
                          </Typography>
                          <Typography fontSize={11} sx={{ color: '#94a3b8', mt: 0.25 }}>
                            <LocalText>Items currently eligible for this campaign.</LocalText>
                          </Typography>
                        </Box>
                        {canEditFreeTrial && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={() => navigate('/items/free-trial/add')}
                            sx={{
                              background: '#059669', color: 'white', fontWeight: 700, fontSize: 12,
                              textTransform: 'none', borderRadius: 2, px: 2.5, py: 1.125, boxShadow: 'none',
                              '&:hover': { background: '#047857' },
                            }}
                          >
                            <LocalText>Add Items</LocalText>
                          </Button>
                        )}
                      </Box>

                      <Box sx={{ p: 2 }}>
                        <StatusTabs
                          items={freeTrialItems}
                          counts={freeTrialItemCounts}
                          value={freeTrialItemStatusTab}
                          onChange={setFreeTrialItemStatusTab}
                          sx={{ mb: 2 }}
                          highlightTab={freeTrialItemHighlight.tab}
                          highlightToken={freeTrialItemHighlight.token}
                        />

                        {freeTrialItemRows.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography fontSize={12} sx={{ color: '#94a3b8' }}>
                              <LocalText>No items in this status. Click "Add Items" to select from your catalog.</LocalText>
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                            {freeTrialItemRows.map((row) => (
                              <FreeTrialListedItemCard
                                key={`${row._viewType}-${row.id ?? row._liveId}`}
                                row={row}
                                item={row.packageId != null
                                  ? packages.find((p) => p.packageId === row.packageId)
                                  : items.find((it) => it.itemId === row.itemId)}
                                itemTypes={itemTypes}
                                canEdit={canEditFreeTrial}
                                onViewProposal={(record, liveId) => setFreeTrialViewProposal({ open: true, record, liveId, contentType: CONTENT_TYPES.FREE_TRIAL_ITEM })}
                                onWithdrawn={(r) => { reloadFreeTrialItems(); flashFreeTrialItemTab(r.approvalActionType === 'create' ? 'draft' : 'live'); }}
                                onSubmitted={() => { reloadFreeTrialItems(); flashFreeTrialItemTab('pending'); }}
                                onRemove={(r) => setDeleteTarget({ type: CONTENT_TYPES.FREE_TRIAL_ITEM, data: r })}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  )}
                </>
              );
            })()}

            {/* ── FREE TRIAL CLAIMS TAB ────────────────────────────────── */}
            {activeTab === 'freeTrialClaims' && (
              <Paper sx={{ ...cardSx, overflow: 'hidden' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid rgba(226,232,240,0.8)' }}>
                  <Typography fontWeight={700} fontSize={14} sx={{ color: '#334155' }}>
                    <LocalText>Free Trial Claims</LocalText>
                  </Typography>
                  <Typography fontSize={11} sx={{ color: '#94a3b8', mt: 0.25 }}>
                    <LocalText>Claim history only — kept separate from real purchase/transaction history.</LocalText>
                  </Typography>
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><LocalText>User</LocalText></TableCell>
                        <TableCell><LocalText>Item</LocalText></TableCell>
                        <TableCell><LocalText>Claimed At</LocalText></TableCell>
                        <TableCell><LocalText>Status</LocalText></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {freeTrialClaims.map((claim) => (
                        <TableRow key={claim.grantId}>
                          <TableCell sx={{ fontSize: 12 }}>{claim.userName || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {claim.itemName?.values?.[0]?.value
                              ?? (claim.packageId != null ? `Package ${claim.packageId}` : `Item ${claim.itemId}`)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{fmtDt(claim.claimedAt)}</TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={{
                                fontSize: 11, fontWeight: 700, borderRadius: 999, px: 1.25, py: 0.375,
                                ...(claim.liveStatus === 'active'
                                  ? STATUS_CHIP.Active
                                  : STATUS_CHIP.Expired),
                              }}
                            >
                              {claim.liveStatus}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {freeTrialClaims.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ fontSize: 12, color: '#94a3b8', py: 3 }}>
                            <LocalText>No free trial claims yet.</LocalText>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            )}

            {/* ── PRIZES TAB ────────────────────────────────────────────── */}
            {/* Same content-approval lifecycle as the Item Catalog tab above (ItemCard,
                StatusTabs, submitApproval/approve/reject) — just scoped to
                items.filter(isPrizeOnly) instead of the normal catalog. A brand-new prize
                sits in Draft until submitted and approved, exactly like a normal item;
                only an APPROVED prize is ever offered by the Game Announcement grant
                pickers (see sp_Item_SelectPrizeOnlyBySpace's own status filter). */}
            {activeTab === 'prizes' && (
              <>
                <Box
                  sx={{
                    ...cardSx, p: 2,
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
                  }}
                >
                  <Box>
                    <Typography fontWeight={700} fontSize={14} sx={{ color: '#334155' }}>
                      <LocalText>Prize Catalog</LocalText>
                    </Typography>
                    <Typography fontSize={11} sx={{ color: '#94a3b8', mt: 0.25 }}>
                      <LocalText>Prize-only items assignable to game winners — never listed for purchase. Must be approved before it can be granted.</LocalText>
                    </Typography>
                  </Box>
                  {canEdit && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { setEditingPrizeItem(null); setPrizeModalOpen(true); }}
                      sx={{
                        background: '#2563eb',
                        color: 'white', fontWeight: 700, fontSize: 12, textTransform: 'none',
                        borderRadius: 2, px: 2.5, py: 1.125, whiteSpace: 'nowrap',
                        boxShadow: '0 2px 8px rgba(37,99,235,0.35)',
                        '&:hover': { background: '#1d4ed8', boxShadow: '0 4px 12px rgba(37,99,235,0.45)' },
                      }}
                    >
                      <LocalText>Add Prize</LocalText>
                    </Button>
                  )}
                </Box>

                <StatusTabs
                  items={items.filter(it => it.isPrizeOnly)}
                  counts={prizeCounts}
                  value={prizeStatusTab}
                  onChange={setPrizeStatusTab}
                  sx={{ mb: 0, mt: 1 }}
                  highlightTab={prizeHighlight.tab}
                  highlightToken={prizeHighlight.token}
                />

                {filteredPrizes.length > 0 ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)',
                      },
                      gap: 2.5,
                    }}
                  >
                    {filteredPrizes.map(item => (
                      <ItemCard
                        key={item.itemId}
                        item={item}
                        itemTypes={itemTypes}
                        onEdit={(it, proposeMode) => { setEditingPrizeItem(it); setPrizeModalReadOnly(false); setPrizeProposeMode(!!proposeMode); setPrizeModalOpen(true); }}
                        onView={it => { setEditingPrizeItem(it); setPrizeModalReadOnly(true); setPrizeProposeMode(false); setPrizeModalOpen(true); }}
                        onDelete={(it, proposeMode) => proposeMode ? setProposeDeletePrize(it) : setDeleteTarget({ type: CONTENT_TYPES.ITEM, data: it })}
                        onEditRejected={canEdit ? handlePrizeEditRejected : undefined}
                        onSuccess={async (targetTab) => {
                          const refreshed = await getItemById(item.itemId).catch(() => null);
                          if (refreshed) setItems(prev => prev.map(it => it.itemId === refreshed.itemId ? refreshed : it));
                          if (targetTab) flashPrizeTab(targetTab);
                        }}
                        showActions={canEdit}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      ...cardSx,
                      py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 64, height: 64, bgcolor: '#f1f5f9', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 2, boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
                      }}
                    >
                      <CardGiftcardOutlinedIcon sx={{ fontSize: 28, color: '#94a3b8' }} />
                    </Box>
                    <Typography fontSize={13} sx={{ color: '#94a3b8' }}>
                      <LocalText>No prize items in this status.</LocalText>
                    </Typography>
                  </Box>
                )}
              </>
            )}

          </>
        )}

      </Box>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      <ItemModal
        open={itemModalOpen}
        onClose={() => { setItemModalOpen(false); setEditingItem(null); setItemModalReadOnly(false); setItemProposeMode(false); }}
        onSave={handleItemSave}
        initialData={editingItem}
        readOnly={itemModalReadOnly}
        itemTypes={itemTypes}
        taxCategories={taxCategories}
        merchandiseTypes={merchandiseTypes}
        merchandiseCategories={merchandiseCategories}
        ticketTypes={ticketTypes}
        events={events}
        onAddTaxClass={handleAddTaxClass}
        onRefreshMerchandiseTypes={async () => {
          const data = await getMerchandiseTypes().catch(() => null);
          if (Array.isArray(data) && data.length > 0) setMerchandiseTypes(data);
        }}
        onRefreshMerchandiseCategories={async () => {
          const data = await getMerchandiseCategories().catch(() => null);
          if (Array.isArray(data) && data.length > 0) setMerchandiseCategories(data);
        }}
      />

      {/* Separate instance (rather than threading prize-mode state through the Item
          Catalog modal above) so the Prizes tab's own create/edit/propose/read-only state
          can't interact with that tab's. Full create/edit/propose parity with Item
          Catalog's own instance below — forcePrizeOnly just always forces
          isPrizeOnly:true/baseCost:0 in the payload (immutable after creation) and hides
          price/tax fields; type selection is otherwise identical to the main Item Catalog
          modal (same filtered itemTypes list, same subtype lookups), since a prize can be
          any of Merchandise/Ticket/Badge — "prize" only means is_price_only=true. */}
      <ItemModal
        open={prizeModalOpen}
        onClose={() => { setPrizeModalOpen(false); setEditingPrizeItem(null); setPrizeModalReadOnly(false); setPrizeProposeMode(false); }}
        onSave={handlePrizeSave}
        initialData={editingPrizeItem}
        readOnly={prizeModalReadOnly}
        forcePrizeOnly
        itemTypes={itemTypes}
        taxCategories={taxCategories}
        merchandiseTypes={merchandiseTypes}
        merchandiseCategories={merchandiseCategories}
        ticketTypes={ticketTypes}
        events={events}
        onAddTaxClass={handleAddTaxClass}
        onRefreshMerchandiseTypes={async () => {
          const data = await getMerchandiseTypes().catch(() => null);
          if (Array.isArray(data) && data.length > 0) setMerchandiseTypes(data);
        }}
        onRefreshMerchandiseCategories={async () => {
          const data = await getMerchandiseCategories().catch(() => null);
          if (Array.isArray(data) && data.length > 0) setMerchandiseCategories(data);
        }}
      />

      <DiscountModal
        open={discountModalOpen}
        onClose={() => { setDiscountModalOpen(false); setEditingDiscount(null); setDiscountProposeMode(false); setDiscountModalReadOnly(false); }}
        onSave={handleDiscountSave}
        initialData={editingDiscount}
        items={items}
        taxCategories={taxCategories}
        proposeMode={discountProposeMode}
        readOnly={discountModalReadOnly}
      />

      {discountViewProposal.record && (
        <ContentPreviewDrawer
          open={discountViewProposal.open}
          approvalRecord={discountViewProposal.record}
          onClose={() => setDiscountViewProposal({ open: false, record: null, liveId: null })}
          readOnly
          onEdit={canEdit && discountViewProposal.record?.status === 'rejected' ? (snap) => {
            const liveId = discountViewProposal.liveId;
            setDiscountViewProposal({ open: false, record: null, liveId: null });
            handleDiscountEditRejected(snap, liveId);
          } : undefined}
        />
      )}

      {freeTrialViewProposal.record && (
        <ContentPreviewDrawer
          open={freeTrialViewProposal.open}
          approvalRecord={freeTrialViewProposal.record}
          onClose={() => setFreeTrialViewProposal({ open: false, record: null, liveId: null, contentType: null })}
          readOnly
        />
      )}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        severity="danger"
        title={
          deleteTarget?.type === CONTENT_TYPES.ITEM ? 'Delete Item'
            : deleteTarget?.type === CONTENT_TYPES.FREE_TRIAL_ITEM
              ? (deleteTarget?.data?.packageId != null ? 'Remove Free Trial Package' : 'Remove Free Trial Item')
            : 'Remove Campaign'
        }
        message={
          deleteTarget?.type === CONTENT_TYPES.ITEM
            ? `Remove "${deleteTarget?.data?.nameLocalizations?.values?.[0]?.value ?? deleteTarget?.data?.name ?? 'this item'}" from the catalog?`
            : deleteTarget?.type === CONTENT_TYPES.FREE_TRIAL_ITEM
              ? (deleteTarget?.data?.packageId != null
                  ? `Remove "${packages.find(p => p.packageId === deleteTarget?.data?.packageId)?.nameLocalizations?.values?.[0]?.value ?? `Package ${deleteTarget?.data?.packageId}`}" from the free trial list?`
                  : `Remove "${items.find(it => it.itemId === deleteTarget?.data?.itemId)?.nameLocalizations?.values?.[0]?.value ?? `Item ${deleteTarget?.data?.itemId}`}" from the free trial list?`)
              : `Remove "${deleteTarget?.data?.nameLocalizations?.values?.[0]?.value ?? 'this campaign'}" from "${items.find(it => it.itemId === deleteTarget?.data?.itemId)?.nameLocalizations?.values?.[0]?.value ?? 'this item'}"?`
        }
        subtitle={
          deleteTarget?.type === CONTENT_TYPES.FREE_TRIAL_ITEM
            ? 'If this item is already live, removal will need manager approval — you can add it back any time.'
            : 'This action cannot be undone.'
        }
        confirmLabel={deleteTarget?.type === CONTENT_TYPES.FREE_TRIAL_ITEM ? 'Remove' : 'Delete'}
        cancelLabel="Cancel"
      />

      <ConfirmModal
        open={Boolean(proposeDeleteItem)}
        onCancel={() => setProposeDeleteItem(null)}
        onConfirm={confirmItemProposeDelete}
        severity="warning"
        title="Propose Deletion"
        message={`Submit a delete request for "${proposeDeleteItem?.nameLocalizations?.values?.[0]?.value ?? proposeDeleteItem?.name ?? 'this item'}"? The item remains available until an approver approves.`}
        confirmLabel="Submit for Approval"
      />

      <ConfirmModal
        open={Boolean(proposeDeletePrize)}
        onCancel={() => setProposeDeletePrize(null)}
        onConfirm={confirmPrizeProposeDelete}
        severity="warning"
        title="Propose Deletion"
        message={`Submit a delete request for "${proposeDeletePrize?.nameLocalizations?.values?.[0]?.value ?? proposeDeletePrize?.name ?? 'this prize item'}"? The item remains available until an approver approves.`}
        confirmLabel="Submit for Approval"
      />

    </ContentArea>
  );
};

export default Items;
